"use client";

/* eslint-disable react/no-array-index-key */

import React, { memo, useMemo } from "react";

import type {
  ApplyPatchCallContentBlock,
  ApplyPatchCallOutputContentBlock,
  BashCodeExecutionToolResultContentBlock,
  ContentBlock,
  ContentBlockType,
  ContentTypeMap,
  DisplayMessage,
  McpApprovalResponseContentBlock,
  ServerToolUseContentBlock,
  ShellCallContentBlock,
  ShellCallOutputContentBlock,
  TextEditorCodeExecutionToolResultContentBlock,
} from "../types";
import { cn } from "../utils";

import {
  ApplyPatchCallBlock,
  ApplyPatchCallOutputBlock,
} from "./blocks/ApplyPatchBlocks";
import { BashResultBlock } from "./blocks/BashResultBlock";
import { CodeBlock } from "./blocks/CodeBlock";
import { CodeExecutionResultBlock } from "./blocks/CodeExecutionResultBlock";
import { ErrorBlock } from "./blocks/ErrorBlock";
import {
  McpApprovalRequestBlock,
  McpApprovalResponseBlock,
  McpCallBlock,
  McpListToolsBlock,
} from "./blocks/McpBlocks";
import {
  ImageUrlBlock,
  MediaBlock,
  MediaVariableBlock,
  OutputMediaBlock,
  WebSearchResultBlock,
} from "./blocks/MediaBlocks";
import { ShellCallBlock, ShellCallOutputBlock } from "./blocks/ShellBlocks";
import { TextBlock } from "./blocks/TextBlock";
import { TextEditorResultBlock } from "./blocks/TextEditorResultBlock";
import { ThinkingBlock } from "./blocks/ThinkingBlock";
import { ServerToolUseBlock, ToolUseBlock } from "./blocks/ToolUseBlock";

// ---------------------------------------------------------------------------
// Approval context helpers
// ---------------------------------------------------------------------------

/**
 * Build a map of approval_request_id → approve boolean from all messages.
 * This is used to resolve the approval status of MCP calls and approval
 * requests across the entire conversation.
 */
export function buildApprovalMap(
  allMessages?: DisplayMessage[],
): Map<string, boolean> {
  const map = new Map<string, boolean>();
  if (!allMessages) {
    return map;
  }
  for (const msg of allMessages) {
    for (const block of msg.content) {
      if (block.type === "mcp_approval_response") {
        const resp = block as McpApprovalResponseContentBlock;
        map.set(resp.approval_request_id, resp.approve);
      }
    }
  }
  return map;
}

/**
 * Build a set of resolved approval request IDs from all messages.
 */
export function buildResolvedApprovalIds(
  allMessages?: DisplayMessage[],
): Set<string> {
  const set = new Set<string>();
  if (!allMessages) {
    return set;
  }
  for (const msg of allMessages) {
    for (const block of msg.content) {
      if (block.type === "mcp_approval_response") {
        const resp = block as McpApprovalResponseContentBlock;
        set.add(resp.approval_request_id);
      }
    }
  }
  return set;
}

// ---------------------------------------------------------------------------
// Block Override Types
// ---------------------------------------------------------------------------

/**
 * Context passed to every block override function so the override can react
 * to streaming state, approval maps, etc. without needing to compute them.
 */
export interface BlockOverrideContext {
  /** Index of this block within the content array. */
  index: number;
  /** Whether the parent message is currently streaming. */
  isStreaming?: boolean;
  /** Approval map built from all messages (only present when mcp_call blocks exist). */
  approvalMap?: Map<string, boolean>;
  /** Set of resolved approval request IDs (only present when mcp_approval_request blocks exist). */
  resolvedApprovalIds?: Set<string>;
  /** The full content array (useful for peeking at adjacent blocks). */
  allBlocks: ContentBlock[];
}

/**
 * A per-block-type render override function.
 *
 * - Return a `ReactNode` to replace the default renderer entirely.
 * - Return `undefined` (or don't return anything) to fall through to the
 *   default renderer built into `ContentBlocks`.
 *
 * This allows consumers (e.g. the Playground) to override only the block
 * types that need custom behaviour (editable text, interactive MCP approval,
 * etc.) while getting every other block type rendered by the shared library
 * for free.
 */
export type BlockOverrideFn<K extends ContentBlockType> = (
  block: ContentTypeMap[K],
  ctx: BlockOverrideContext,
) => React.ReactNode | undefined;

/**
 * Map of content block type → optional override render function.
 *
 * Usage:
 * ```tsx
 * const overrides: BlockOverrides = {
 *   text: (block, ctx) => <MyEditableText block={block} index={ctx.index} />,
 *   thinking: (block, ctx) => <MyThinkingEditor block={block} />,
 *   // all other types fall through to the default renderer
 * };
 *
 * <ContentBlocks content={blocks} blockOverrides={overrides} />
 * ```
 */
export type BlockOverrides = {
  [K in ContentBlockType]?: BlockOverrideFn<K>;
};

// ---------------------------------------------------------------------------
// ContentBlocks
// ---------------------------------------------------------------------------

export interface ContentBlocksProps {
  content: ContentBlock[];
  isStreaming?: boolean;
  className?: string;
  /**
   * All messages in the conversation. When provided, enables cross-message
   * approval resolution for MCP call and approval request blocks.
   */
  allMessages?: DisplayMessage[];
  /**
   * Error information to display at the end of the content blocks.
   */
  error?: { message: string; verbose?: string } | string | null;
  /**
   * Per-type render overrides. When a block's type has a matching key,
   * the override function is called instead of the default renderer.
   * Return `undefined` from the override to fall through to the default.
   */
  blockOverrides?: BlockOverrides;
}

/**
 * Renders an array of ContentBlocks sequentially — each block rendered
 * individually in the order it arrives.
 *
 * Consumers can supply `blockOverrides` to replace the default renderer
 * for specific block types while inheriting the default for everything else.
 */
export const ContentBlocks = memo(
  ({
    content,
    isStreaming,
    className,
    allMessages,
    error,
    blockOverrides,
  }: ContentBlocksProps) => {
    const hasMcpCall = content.some((b) => b.type === "mcp_call");
    const hasMcpApprovalReq = content.some(
      (b) => b.type === "mcp_approval_request",
    );

    const approvalMap = useMemo(
      () => (hasMcpCall ? buildApprovalMap(allMessages) : undefined),
      [hasMcpCall, allMessages],
    );

    const resolvedIds = useMemo(
      () =>
        hasMcpApprovalReq ? buildResolvedApprovalIds(allMessages) : undefined,
      [hasMcpApprovalReq, allMessages],
    );

    return (
      <div className={cn("flex flex-col gap-4", className)}>
        {content.map((block, i) => {
          // --- Check for a consumer-provided override ---
          if (blockOverrides) {
            const overrideFn = blockOverrides[block.type as ContentBlockType];
            if (overrideFn) {
              const ctx: BlockOverrideContext = {
                index: i,
                isStreaming,
                approvalMap,
                resolvedApprovalIds: resolvedIds,
                allBlocks: content,
              };
              // The override fn is typed per-block-type but we need to call it
              // generically here. The cast is safe because the discriminant
              // (`block.type`) guarantees the block matches the expected type.
              const result = (overrideFn as BlockOverrideFn<ContentBlockType>)(
                block as ContentTypeMap[ContentBlockType],
                ctx,
              );
              if (result !== undefined) {
                return (
                  <React.Fragment key={`${block.type}-${i}`}>
                    {result}
                  </React.Fragment>
                );
              }
              // result === undefined → fall through to the default renderer
            }
          }

          // --- Default renderers ---
          switch (block.type) {
            case "text":
              return (
                <TextBlock
                  key={`text-${i}`}
                  blocks={[block]}
                  isStreaming={isStreaming}
                />
              );
            case "thinking":
              return (
                <ThinkingBlock
                  key={`thinking-${i}`}
                  block={block}
                  isStreaming={isStreaming}
                />
              );
            case "code":
              return (
                <CodeBlock
                  key={`code-${i}`}
                  block={block}
                  isStreaming={isStreaming}
                />
              );
            case "code_execution_result":
              return (
                <CodeExecutionResultBlock
                  key={`exec-${i}`}
                  block={block}
                  isStreaming={isStreaming}
                />
              );
            case "server_tool_use":
              return (
                <ServerToolUseBlock
                  key={`stu-${i}`}
                  block={block as ServerToolUseContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "bash_code_execution_tool_result":
              return (
                <BashResultBlock
                  key={`bash-${i}`}
                  block={block as BashCodeExecutionToolResultContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "text_editor_code_execution_tool_result":
              return (
                <TextEditorResultBlock
                  key={`te-${i}`}
                  block={block as TextEditorCodeExecutionToolResultContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "shell_call":
              return (
                <ShellCallBlock
                  key={`shell-${block.id || i}`}
                  block={block as ShellCallContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "shell_call_output":
              return (
                <ShellCallOutputBlock
                  key={`shellout-${block.id || i}`}
                  block={block as ShellCallOutputContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "apply_patch_call":
              return (
                <ApplyPatchCallBlock
                  key={`patch-${block.id || i}`}
                  block={block as ApplyPatchCallContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "apply_patch_call_output":
              return (
                <ApplyPatchCallOutputBlock
                  key={`patchout-${block.id || i}`}
                  block={block as ApplyPatchCallOutputContentBlock}
                  isStreaming={isStreaming}
                />
              );
            case "output_media":
              return <OutputMediaBlock key={`om-${i}`} block={block} />;
            case "media":
              return <MediaBlock key={`media-${i}`} block={block} />;
            case "image_url":
              return <ImageUrlBlock key={`img-${i}`} block={block} />;
            case "media_variable":
              return <MediaVariableBlock key={`mv-${i}`} block={block} />;
            case "tool_use":
              return (
                <ToolUseBlock
                  key={`tu-${i}`}
                  block={block}
                  isStreaming={isStreaming}
                />
              );
            case "web_search_tool_result":
              return <WebSearchResultBlock key={`ws-${i}`} block={block} />;
            case "mcp_list_tools":
              return <McpListToolsBlock key={`mcpl-${i}`} block={block} />;
            case "mcp_call":
              return (
                <McpCallBlock
                  key={`mcpc-${i}`}
                  block={block}
                  approvalMap={approvalMap}
                />
              );
            case "mcp_approval_request":
              return (
                <McpApprovalRequestBlock
                  key={`mcpar-${i}`}
                  block={block}
                  isResolved={
                    resolvedIds ? resolvedIds.has(block.id ?? "") : false
                  }
                />
              );
            case "mcp_approval_response":
              return (
                <McpApprovalResponseBlock key={`mcparesp-${i}`} block={block} />
              );
            default:
              return null;
          }
        })}
        {error && <ErrorBlock error={error} />}
      </div>
    );
  },
);
